import {
  Autocomplete,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { getCompanyConfig } from 'lib/api/company';
import { createBusinessContact } from 'lib/api/contacts';
import { ContactBody } from 'lib/api/contacts/types';
import { getIntegration } from 'lib/api/integrations';
import {
  COUNTRY_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  PROFESSION_OPTIONS,
  ROLE_OPTIONS,
} from 'lib/consts/contactFields';
import CustomButton from 'lib/designSystem/Button';
import {
  buildValidation,
  isValidChileanPhone,
  isValidEmail,
  showAlert,
  validaRut,
} from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

type Props = {
  businessIdentifier: string | null | undefined;
  onCreated?: () => void;
};

const emptyContact: ContactBody = {
  name: '',
  email: '',
  phone: '',
  role: '',
  customRole: '',
  observation: '',
  identifier: '',
  country: undefined,
  profession: '',
  maritalStatus: undefined,
  businessIdentifier: undefined,
};

const CreateContact: React.FC<Props> = ({ businessIdentifier, onCreated }) => {
  const dispatch = useDispatch();
  const { mutate, isPending } = createBusinessContact(dispatch);
  const { data: integration } = getIntegration(dispatch, 'digital-signature');
  const hasDs = integration?.status;

  const [contact, setContact] = useState<ContactBody>(emptyContact);
  const [disableValidation, setDisableValidation] = useState(false);

  useEffect(() => {
    setContact((prev) => ({
      ...prev,
      businessIdentifier: businessIdentifier || undefined,
    }));
  }, [businessIdentifier]);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await getCompanyConfig(
          dispatch,
          'system_disabled_contact_validation',
        );
        if (typeof response === 'string') {
          setDisableValidation(response === '1');
        }
      } catch (error) {
        console.error(error);
      }
    };

    void fetchConfig();
  }, [dispatch]);

  const { hasError: nameHasError, message: nameErrorMessage } = buildValidation(
    !contact?.name?.length,
    false,
    'Nombre obligatorio',
    '',
  );

  const { hasError: emailHasError, message: emailErrorMessage } =
    buildValidation(
      !contact?.email?.length,
      !!contact?.email?.length && !isValidEmail(contact?.email),
      'Email obligatorio',
      'Email inválido',
    );

  const { hasError: phoneHasError, message: phoneErrorMessage } =
    buildValidation(
      false,
      !!contact?.phone?.length &&
        !!contact.phone &&
        !isValidChileanPhone(contact.phone),
      '',
      'Teléfono inválido (56XXXXXXXX)',
    );

  const { hasError: rutHasError, message: rutErrorMessage } = buildValidation(
    false,
    !!contact?.identifier?.length &&
      !!contact.identifier &&
      !validaRut(contact.identifier),
    '',
    'RUT inválido',
  );

  const nameError = disableValidation ? false : nameHasError;
  const emailError = disableValidation ? false : emailHasError;
  const phoneError = disableValidation ? false : phoneHasError;
  const rutError = disableValidation ? false : rutHasError;

  const getFirstErrorMessage = () => {
    if (nameError) return nameErrorMessage || 'Nombre inválido';
    if (emailError) return emailErrorMessage || 'Email inválido';
    if (phoneError) return phoneErrorMessage || 'Teléfono inválido';
    if (rutError) return rutErrorMessage || 'RUT inválido';
    return 'Revisa los datos ingresados.';
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (!businessIdentifier) {
      showAlert(
        dispatch,
        'error',
        'No se encontró el identificador de empresa.',
      );
      return;
    }

    if (!disableValidation) {
      if (nameError || emailError || phoneError || rutError) {
        showAlert(dispatch, 'error', getFirstErrorMessage());
        return;
      }
    }

    const {
      name,
      email,
      phone,
      role,
      customRole,
      observation,
      identifier,
      country,
      profession,
      maritalStatus,
    } = contact;

    const body: ContactBody = {
      name,
      email,
      phone,
      role,
      customRole,
      observation,
      identifier,
      country,
      profession,
      maritalStatus,
      businessIdentifier,
    };

    const filteredBody = Object.fromEntries(
      Object.entries(body).filter(([, value]) => value != null && value !== ''),
    ) as ContactBody;

    mutate(filteredBody, {
      onSuccess: () => {
        if (onCreated) onCreated();
        setContact(emptyContact);
      },
    });
  };

  return (
    <FormControl fullWidth margin="dense">
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Nombre"
            value={contact.name ?? ''}
            fullWidth
            error={nameError}
            helperText={disableValidation ? '' : nameErrorMessage}
            onChange={(e) =>
              setContact({ ...contact, name: e.target.value || '' })
            }
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Email"
            value={contact.email ?? ''}
            fullWidth
            error={emailError}
            helperText={disableValidation ? '' : emailErrorMessage}
            onChange={(e) =>
              setContact({ ...contact, email: e.target.value || '' })
            }
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Teléfono"
            value={contact.phone ?? ''}
            fullWidth
            error={phoneError}
            helperText={disableValidation ? '' : phoneErrorMessage}
            onChange={(e) =>
              setContact({ ...contact, phone: e.target.value || '' })
            }
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="RUT"
            value={contact.identifier ?? ''}
            fullWidth
            error={rutError}
            helperText={disableValidation ? '' : rutErrorMessage}
            onChange={(e) =>
              setContact({
                ...contact,
                identifier: e.target.value || '',
              })
            }
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel id="role-label">{'Rol'}</InputLabel>
            <Select
              labelId="role-label"
              id="role-select"
              fullWidth
              value={contact.role ?? ''}
              onChange={(e) =>
                setContact({
                  ...contact,
                  role: e.target.value ? String(e.target.value) : '',
                })
              }
              label={'Rol'}
            >
              {ROLE_OPTIONS.map((role) => (
                <MenuItem key={role} value={role}>
                  {role}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {contact.role === 'Otro' && (
          <Grid item xs={12} sm={6}>
            <TextField
              label="Rol personalizado"
              value={contact.customRole ?? ''}
              fullWidth
              onChange={(e) =>
                setContact({
                  ...contact,
                  customRole: e.target.value || '',
                })
              }
            />
          </Grid>
        )}

        {hasDs && (
          <>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={Object.keys(MARITAL_STATUS_OPTIONS)}
                value={contact.maritalStatus ?? null}
                onChange={(_event, newValue) =>
                  setContact({
                    ...contact,
                    maritalStatus: newValue ?? undefined,
                  })
                }
                getOptionLabel={(option) =>
                  MARITAL_STATUS_OPTIONS[
                    option as keyof typeof MARITAL_STATUS_OPTIONS
                  ] ?? option
                }
                renderInput={(params) => (
                  <TextField {...params} label="Estado civil" fullWidth />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={Object.keys(COUNTRY_OPTIONS)}
                value={contact.country ?? null}
                onChange={(_event, newValue) =>
                  setContact({
                    ...contact,
                    country: newValue ?? undefined,
                  })
                }
                getOptionLabel={(option) =>
                  COUNTRY_OPTIONS[option as keyof typeof COUNTRY_OPTIONS] ??
                  option
                }
                renderInput={(params) => (
                  <TextField {...params} label="País" fullWidth />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                freeSolo
                options={PROFESSION_OPTIONS as readonly string[]}
                value={contact.profession ?? ''}
                onChange={(_event, newValue) =>
                  setContact({
                    ...contact,
                    profession: newValue ?? undefined,
                  })
                }
                onInputChange={(_event, newInputValue, reason) => {
                  if (reason === 'input' || reason === 'clear') {
                    setContact({
                      ...contact,
                      profession: newInputValue || undefined,
                    });
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Profesión" fullWidth />
                )}
              />
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <TextField
            label="Observaciones"
            value={contact.observation ?? ''}
            fullWidth
            multiline
            rows={3}
            onChange={(e) =>
              setContact({
                ...contact,
                observation: e.target.value || '',
              })
            }
          />
        </Grid>
        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <CustomButton
            color="primary"
            onClick={handleSubmit}
            disabled={isPending}
            loading={isPending}
          >
            Crear contacto
          </CustomButton>
        </Grid>
      </Grid>
    </FormControl>
  );
};

export default CreateContact;
